<template>
    <div class="modal-container member-roster-unavailable-days-modal">
        <div class="modal-header">
            <h4 class="modal-title">Update Unavailable Days</h4>
            <button
                type="button"
                class="md-button md-simple md-just-icon md-round modal-default-button md-theme-default"
                @click.stop="$modal.hide()"
            >
                <div class="md-ripple">
                    <div class="md-button-content"><i class="md-icon md-icon-font md-theme-default">clear</i></div>
                </div>
            </button>
        </div>
        <div class="modal-body">
            <p v-if="!selectedMemberIds.length" class="no-selection">No team members selected.</p>
            <div v-else>
                <p class="selected-count">
                    <strong>{{ selectedMemberIds.length }}</strong>
                    team member(s) selected.
                </p>
                <div class="form-group">
                    <md-datepicker v-model="form.startDate" :md-immediately="true"><label>Start</label></md-datepicker>
                    <small v-if="validation.startDate" class="md-error">Start date is required.</small>
                </div>
                <div class="form-group">
                    <md-datepicker v-model="form.endDate" :md-immediately="true"><label>End</label></md-datepicker>
                    <small v-if="validation.endDate" class="md-error">End date is required.</small>
                    <small v-else-if="validation.endBeforeStart" class="md-error">
                        End date cannot be earlier than start date.
                    </small>
                </div>
                <div class="form-group">
                    <md-field :class="{ 'md-invalid': validation.reason }">
                        <label>Reason (optional)</label>
                        <md-input v-model.trim="form.reason" maxlength="250" />
                        <span class="md-error" v-if="validation.reason">Reason is required.</span>
                    </md-field>
                </div>
                <div class="form-group attachments-group">
                    <label>
                        Attachments
                        <span class="accepted-filetypes">(Allowed: {{ acceptedFileTypesComputed }})</span>
                    </label>
                    <document-file-upload
                        :documents="attachments"
                        :file-label="'files or images'"
                        :accepted-file-types="acceptedFileTypes"
                        @upload-success="onAttachmentUpload"
                    />
                </div>
                <p v-if="errorMessage" class="md-error" style="margin-top: 4px">{{ errorMessage }}</p>
            </div>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Close</md-button>
            <md-button
                class="dialog-button md-primary"
                :disabled="!isValid || isSubmitting"
                @click.stop="submit"
                id="btnConfirmUpdateUnavailableDays"
            >
                <span v-if="!isSubmitting">Update</span>
                <span v-else>Saving...</span>
            </md-button>
        </div>
    </div>
</template>

<script>
import { DocumentFileUpload } from '@/components';
import { handleRequests } from '@/helpers';
import { BlockBlobClient } from '@azure/storage-blob';

export default {
    name: 'MemberRosterUnavailableDaysModal',
    components: { DocumentFileUpload },
    props: {
        selectedMemberIds: { type: Array, default: () => [] },
        onUpdatedCallback: { type: Function, default: null }
    },
    data() {
        return {
            form: { startDate: null, endDate: null, reason: '' },
            isSubmitting: false,
            validation: {
                startDate: false,
                endDate: false,
                endBeforeStart: false,
                reason: false
            },
            attachments: [],
            attachmentList: [],
            images: [],
            documents: [],
            acceptedFileTypes: '.pdf,.jpg,.jpeg,.png,.webp', // modifying these would need to modify the backend as well, AddBlockoutDaysRequest.Attachment accepted types
            errorMessage: ''
        };
    },
    computed: {
        isValid() {
            return !!(
                this.selectedMemberIds.length &&
                this.form.startDate &&
                this.form.endDate &&
                !this.validation.endBeforeStart
            );
        },
        acceptedFileTypesComputed() {
            return this.acceptedFileTypes
                .split(',')
                .map((f) => f.replace('.', '').toUpperCase())
                .join(', ');
        }
    },
    methods: {
        notify(message, type = 'info') {
            if (this.$notify) 
                this.$notify({ message, type, verticalAlign: 'bottom', horizontalAlign: 'right' });
        },
        onAttachmentUpload(filelist) {
            const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
            const allowedExtensions = this.acceptedFileTypes
                .split(',')
                .map((e) => e.trim().toLowerCase())
                .filter((e) => !!e);
            const potentialDocuments = filelist.filter((f) => !imageTypes.includes(f.type));
            const rawImages = filelist.filter((f) => imageTypes.includes(f.type));
            const validDocs = [];
            const rejected = [];
            potentialDocuments.forEach((f) => {
                const name = f.name || '';
                const idx = name.lastIndexOf('.');
                const ext = idx >= 0 ? name.slice(idx).toLowerCase() : '';
                if (allowedExtensions.includes(ext)) {
                    validDocs.push(f);
                } else {
                    rejected.push(name);
                }
            });
            if (rejected.length) {
                this.notify(`Some files were rejected: ${rejected.join(', ')}`, 'warning');
            }
            this.documents = validDocs.map((f) => ({ file: f, type: f.type }));
            this.images = rawImages.map((f) => ({ file: f, type: f.type }));
            this.attachments = [...validDocs, ...rawImages];
            this.attachmentList = [...this.documents, ...this.images];
        },
        formatDateOnly(date) {
            if (!date) 
                return null;
            const parsedDate = typeof date === 'string' ? new Date(date) : date;
            if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) 
                return null;
            return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`;
        },
        buildPayload() {
            const photoFileNames = this.images.map((i) => i.file.name);
            const documentFileNames = this.documents.map((d) => d.file.name);
            const payload = {
                publicUserIds: this.selectedMemberIds,
                startDate: this.formatDateOnly(this.form.startDate),
                endDate: this.formatDateOnly(this.form.endDate),
                reason: this.form.reason || null
            };
            if (photoFileNames.length || documentFileNames.length)
                payload.attachment = { photoFileNames, documentFileNames };
            return payload;
        },
        async submit() {
            this.errorMessage = '';
            this.validation.startDate = !this.form.startDate;
            this.validation.endDate = !this.form.endDate;
            this.validation.endBeforeStart = false;
            if (this.form.startDate && this.form.endDate) {
                const s = new Date(this.form.startDate);
                const e = new Date(this.form.endDate);
                if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) {
                    this.validation.endBeforeStart = e < s;
                }
            }
            if (!this.isValid) 
                return;
            this.isSubmitting = true;
            try {
                const payload = this.buildPayload();
                const { data } = await handleRequests('/api/team-member-roster-blockoutday/bulkUpdateBlockoutDays', {
                    method: 'post',
                    data: payload
                });
                await this.uploadReturnedAttachments(data?.results);
                this.notify('Unavailable days updated successfully.', 'success');
                this.$emit('updated', { request: payload, response: data });
                if (typeof this.onUpdatedCallback === 'function') {
                    try {
                        this.onUpdatedCallback({ request: payload, response: data });
                    } catch (err) {
                        console.error('onUpdatedCallback failed', err);
                    }
                }
                this.$modal.hide();
            } catch (e) {
                console.error(e);
                const errMsg =
                    (e?.response?.data && (e.response.data.message || e.response.data[0])) ||
                    'Failed to update unavailable days.';
                this.errorMessage = errMsg;
                this.notify(errMsg, 'danger');
            } finally {
                this.isSubmitting = false;
            }
        },
        async uploadReturnedAttachments(results) {
            if (!Array.isArray(results) || !results.length) 
                return;
            const uploadPromises = [];
            results.forEach((r) => {
                const photoUrls = (r.PhotoUploadUrls || r.photoUploadUrls || [])
                    .map((p) => (typeof p === 'string' ? p : p.UploadUrl || p.uploadUrl))
                    .filter(Boolean);
                const documentUrls = (r.DocumentUploadUrls || r.documentUploadUrls || [])
                    .map((d) => (typeof d === 'string' ? d : d.UploadUrl || d.uploadUrl))
                    .filter(Boolean);
                photoUrls.forEach((url, idx) => {
                    const img = this.images[idx];
                    if (url && img?.file) 
                        uploadPromises.push(this.uploadBlob(url, img.file));
                });
                documentUrls.forEach((url, idx) => {
                    const doc = this.documents[idx];
                    if (url && doc?.file) 
                        uploadPromises.push(this.uploadBlob(url, doc.file));
                });
            });
            if (!uploadPromises.length) 
                return;
            await Promise.allSettled(uploadPromises);
            this.images = [];
            this.documents = [];
            this.attachments = [];
            this.attachmentList = [];
        },
        async uploadBlob(sasUrl, file) {
            try {
                const client = new BlockBlobClient(sasUrl);
                await client.uploadData(file, {
                    blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' }
                });
            } catch (err) {
                console.error('Upload failed for', file?.name, err);
                this.notify(`Upload failed for ${file?.name}`, 'warning');
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.member-roster-unavailable-days-modal {
    .modal-header {
        color: #3c4858;
        font-weight: 600;
    }
    .modal-body {
        max-height: 60vh;
        overflow-y: auto;
        padding: 24px 24px 16px;
    }
    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }
    .attachments-group {
        label {
            font-weight: 600;
        }
        .accepted-filetypes {
            font-weight: normal;
            font-size: 12px;
            margin-left: 4px;
            color: #607d8b;
        }
    }
    .md-error {
        color: #f44336;
    }
}
</style>
